import { type DeliveryNoteOcrResult } from "@finway-group/shared/lib/models"
import { DocumentTypeEnum } from "@finway-group/shared/lib/models/common/documentType.enum"
import { type DocumentCoreData } from "@finway-group/shared/lib/models/file/documentCoreData"
import { type InboxDeliveryNote } from "@finway-group/shared/lib/models/invoice/inboxInvoice.model"
import {
    PURCHASE_ORDER_DESCRIPTION_MAX_LENGTH,
    PURCHASE_ORDER_NUMBER_MAX_LENGTH,
    PURCHASE_ORDER_NUMBER_MIN_LENGTH,
} from "@finway-group/shared/lib/models/purchaseOrder/purchaseOrder.validation"
import { Button, Col, DatePicker, Form, Input, Modal, Row } from "antd"
import axios from "axios"
import moment from "moment"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { VendorSelection } from "~Components/forms/expenseForm/commonInputFields/VendorSelection"
import { PurchaseOrderSelectionButton } from "~Components/forms/expenseForm/PurchaseOrderSelectionButton"
import { ResponsiveFormLayout } from "~Components/layout/ResponsiveFormLayout"
import { DeliveryNoteLinkModal } from "~Components/modals/DeliveryNoteLink/DeliveryNoteLinkModal"
import { createDeliveryNoteFormRules } from "~Features/pages/deliveryNotes/components/DeliveryNoteForm.rules"
import { LineItemsList } from "~Features/pages/deliveryNotes/components/LineItemsList"
import { PurchaseOrderAddressAutocomplete } from "~Features/pages/purchaseOrders/components/PurchaseOrderAddressAutoComplete"
import { usePurchaseOrdersSelectedForDeliveryNote } from "~Features/pages/purchaseOrders/hooks/usePurchaseOrdersSelected"
import { defaultFilterDeliveryNoteLink } from "~Features/pages/purchaseOrders/queries/const"
import { fetchPurchaseOrdersForLinking } from "~Features/pages/purchaseOrders/queries/purchaseOrder.queries"
import { useModal } from "~Shared/context/modal.context"
import { type PurchaseOrderReference } from "~Shared/models/purchaseOrderReference"
import { useVendor } from "~Shared/queries/vendors/vendor.queries"
import { NotificationService } from "~Shared/services"
import { NotificationTypeEnum } from "~Shared/services/notification.service"
import { OcrService, type VendorReadFileAiResponse } from "~Shared/services/ocr.service"
import { gobdNonCompliantDates } from "~Shared/utils/date.utils"
import { formatAddress, formatDeliveryLocation } from "~Shared/utils/form.utils"
import { mapVendorReadFileAiResponseToVendor } from "~Shared/utils/ocr.utils"
import { FileZone } from "../../purchaseOrders/components/FileZone"
import { useCreateDeliveryNoteMutation, useUpdateDeliveryNoteMutation } from "../queries/deliveryNote.queries"
import { type DeliveryNote, type DeliveryNoteFormValues } from "../types/deliveryNote.interfaces"

const toFormValues = (deliveryNote: DeliveryNote | undefined): DeliveryNoteFormValues | undefined => {
    if (!deliveryNote) {
        return undefined
    }
    return {
        deliveryNoteNumber: deliveryNote.deliveryNoteNumber,
        description: deliveryNote.description,
        dateOfIssue: moment(deliveryNote.dateOfIssue),
        dateOfIssueForm: moment(deliveryNote.dateOfIssue),
        lineItems: deliveryNote.lineItems,
        deliveryLocation: deliveryNote.deliveryLocation ? formatAddress(deliveryNote.deliveryLocation) : "",
        deliveryNoteDocument: deliveryNote.deliveryNoteDocument,
        vendor: deliveryNote.vendor,
    }
}

const runInboxOcrHydration = async (inbox: InboxDeliveryNote, callback: (data: VendorReadFileAiResponse | undefined) => Promise<void>): Promise<void> => {
    const readFileAi = await OcrService.postOcrHandler({
        fileId: inbox.fileId,
        ocrData: inbox.ocrResults,
    })
    callback({
        fileId: inbox.fileId,
        ocrData: inbox.ocrResults,
        vendorId: readFileAi.vendorId,
    })
}

export type InboxDeliveryNoteFormProps = {
    inboxDeliveryNote?: InboxDeliveryNote
    onCreatedFromInbox?: () => Promise<void>
}

type DeliveryNoteFormProps = {
    editMode: boolean
    deliveryNote?: DeliveryNote
    onCancel: () => void
    /** The PO that gets preselected by adding the document directly from the PO detail page */
    preSelectedPurchaseOrder: PurchaseOrderReference | undefined
    /** The delivery note to be edited, omit when creating a new one. */
} & InboxDeliveryNoteFormProps

export const DeliveryNoteForm = ({ editMode, deliveryNote, onCancel, preSelectedPurchaseOrder, inboxDeliveryNote, onCreatedFromInbox }: DeliveryNoteFormProps) => {
    const initialValues = useMemo(() => toFormValues(deliveryNote), [deliveryNote])
    const [currentFile, setCurrentFile] = useState<DocumentCoreData | undefined>()

    const deliveryNoteFormRules = createDeliveryNoteFormRules()

    const [localVendor, setLocalVendor] = useState<string | undefined>(initialValues?.vendor?._id || undefined)
    const { data: selectedVendor } = useVendor(localVendor, true)

    const [form] = Form.useForm<DeliveryNoteFormValues>()

    const [currentFileOcr, setCurrentFileOcr] = useState<VendorReadFileAiResponse | undefined>(undefined)
    const lineItems = Form.useWatch("lineItems", form)

    const { selectedPurchaseOrders, setSelectedPurchaseOrders } = usePurchaseOrdersSelectedForDeliveryNote({
        purchaseOrder: preSelectedPurchaseOrder,
        isNew: !editMode,
        deliveryNote: { _id: deliveryNote?._id ?? "NEW_DELIVERY_NOTE", lineItems: deliveryNote?.lineItems ?? lineItems },
    })
    const shouldShowPurchaseOrderLinkModal = !!selectedPurchaseOrders?.length

    useEffect(() => {
        if (initialValues?.deliveryNoteDocument) {
            setCurrentFile(initialValues?.deliveryNoteDocument)
        }
    }, [initialValues])

    useEffect(() => {
        form.setFieldsValue({
            ...initialValues,
            dateOfIssueForm: moment(initialValues?.dateOfIssue),
        })
    }, [initialValues])

    const prepareData = async () => {
        const values = await form.validateFields()
        const deliveryLocation = formatDeliveryLocation(values.deliveryLocation)

        return {
            deliveryNoteNumber: values.deliveryNoteNumber,
            description: values.description,
            dateOfIssue: values.dateOfIssueForm.toDate(),
            lineItems: values.lineItems || [],
            deliveryLocation,
            deliveryNoteDocument: values.deliveryNoteDocument,
            vendor: values.vendor?._id,
        }
    }

    const { mutateAsync: createDeliveryNote } = useCreateDeliveryNoteMutation()
    const { mutateAsync: updateDeliveryNote } = useUpdateDeliveryNoteMutation()
    const { showModal } = useModal()

    const onSubmit = async () => {
        try {
            const dn = await prepareData()

            let newDeliveryNoteId = deliveryNote?._id
            if (editMode && deliveryNote) {
                await updateDeliveryNote({ id: deliveryNote._id, deliveryNote: dn })
            } else {
                const newDeliveryNote = await createDeliveryNote(dn)
                newDeliveryNoteId = newDeliveryNote._id

                await onCreatedFromInbox?.()
            }
            if (shouldShowPurchaseOrderLinkModal) {
                showModal(DeliveryNoteLinkModal, true, {
                    isShowing: true,
                    deliveryNoteId: newDeliveryNoteId,
                    selectedPurchaseOrders,
                    onCancel,
                })
                return
            }
            onCancel()
        } catch (error) {
            if (error.errorFields) {
                const firstError = error.errorFields[0]
                const fieldKey = firstError.name

                form.scrollToField(fieldKey, { block: "center", behavior: "smooth" })

                return
            }
            if (!axios.isAxiosError(error)) {
                NotificationService.send(NotificationTypeEnum.ERROR, t("error:unknown"), undefined, 3)
            }
        }
    }

    const handleFileChange = (data: DocumentCoreData) => {
        setCurrentFile(data)
        form.setFieldValue("deliveryNoteDocument", data)
    }

    /** @returns `false` if the user cancelled adding the document after being warned about number mismatches, `true` otherwise. */
    const preselectPOByOCR = useCallback(
        async (updatedOcrObject: DeliveryNoteOcrResult | null) => {
            const purchaseOrderNumber = updatedOcrObject?.orderNumber
            if (!purchaseOrderNumber) {
                return true
            }

            return new Promise<boolean>((resolve) => {
                const loadAndSelectPO = async () => {
                    const result = await fetchPurchaseOrdersForLinking({
                        filter: { ...defaultFilterDeliveryNoteLink, purchaseOrderNumber: { eq: purchaseOrderNumber } },
                    })

                    if (result?.docs?.length > 0) {
                        const purchaseOrder = result.docs[0]
                        setSelectedPurchaseOrders([
                            {
                                _id: purchaseOrder._id,
                                purchaseOrderNumber: purchaseOrder.purchaseOrderNumber,
                                alreadyLinked: false,
                            },
                        ])
                    }

                    resolve(true)
                }

                if (preSelectedPurchaseOrder && preSelectedPurchaseOrder.purchaseOrderNumber !== purchaseOrderNumber) {
                    Modal.confirm({
                        title: t("confirm:purchase_order.link_mismatching_delivery_note.title"),
                        content: t("confirm:purchase_order.link_mismatching_delivery_note.message", {
                            poNumberOnDeliveryNote: purchaseOrderNumber,
                            poNumber: preSelectedPurchaseOrder.purchaseOrderNumber,
                        }),
                        okText: t("confirm:purchase_order.link_mismatching_delivery_note.confirm"),
                        cancelText: t("confirm:purchase_order.link_mismatching_delivery_note.cancel"),
                        onOk: () => {
                            resolve(true)
                        },
                        onCancel: () => {
                            resolve(false)
                        },
                    })
                } else {
                    void loadAndSelectPO()
                }
            })
        },
        [setSelectedPurchaseOrders],
    )

    const onVendorChange = (vendorId: string) => {
        setLocalVendor(vendorId)
        form.setFieldValue(["vendor", "_id"], vendorId)
    }

    const handleFileOcrChange = async (data: VendorReadFileAiResponse | undefined) => {
        setCurrentFileOcr(data)

        const ocrData = data?.ocrData as DeliveryNoteOcrResult
        if (!ocrData) {
            return
        }

        const isCancelledByUser = !(await preselectPOByOCR(ocrData))
        if (isCancelledByUser) {
            handleFileRemove()
            setCurrentFileOcr(undefined)
            return
        }

        if (ocrData?.documentNumber) {
            form.setFieldValue("deliveryNoteNumber", ocrData?.documentNumber)
        }
        if (ocrData?.documentDate) {
            form.setFieldValue("dateOfIssueForm", moment(ocrData?.documentDate))
        }

        if (ocrData?.items?.length > 0) {
            const ocrItems: Array<Partial<DeliveryNoteFormValues["lineItems"][number]>> = ocrData?.items.map((item) => ({
                name: item.description || undefined,
                materialCode: item.articleNumber || undefined,
                quantity: item.quantityDelivered || undefined,
                unitOfMeasure: item.quantityUnit || undefined,
            }))
            const hasAnyLink = lineItems?.some((item) => item.purchaseOrderLink)
            if (hasAnyLink) {
                // prevent potentially overwriting the linked items
                form.setFieldValue("lineItems", [...lineItems, ...ocrItems])
            } else {
                form.setFieldValue("lineItems", [...ocrItems])
            }
        }
        if (ocrData?.deliveryAddress) {
            form.setFieldValue("deliveryLocation", {
                addressLine1: ocrData?.deliveryAddress.streetName,
                zipCode: ocrData?.deliveryAddress.zipCode,
                city: ocrData?.deliveryAddress.city,
                country: ocrData?.deliveryAddress.country,
            })
        }

        // vendor
        if (data?.vendorId) {
            onVendorChange(data.vendorId)
        }
    }

    useEffect(() => {
        if (!inboxDeliveryNote) {
            return
        }
        const doc: DocumentCoreData = {
            fileId: inboxDeliveryNote.fileId,
            url: inboxDeliveryNote.url,
            name: inboxDeliveryNote.fileName,
        }
        setCurrentFile(doc)
        form.setFieldValue("deliveryNoteDocument", doc)
        void runInboxOcrHydration(inboxDeliveryNote, handleFileOcrChange)
    }, [inboxDeliveryNote])

    const handleFileRemove = () => {
        setCurrentFile(undefined)
        form.setFieldValue("deliveryNoteDocument", undefined)
    }

    const { t } = useTranslation()

    if (editMode && !initialValues) {
        return null
    }

    const hasLinked = lineItems?.some((item) => item.purchaseOrderLink) ?? false
    const fullyLinked = lineItems?.every((item) => item.purchaseOrderLink) ?? false

    const leftSide = (
        <Form.Item name="deliveryNoteDocument">
            <FileZone
                onChange={handleFileChange}
                onOcrChange={handleFileOcrChange}
                onRemove={handleFileRemove}
                initialFile={currentFile}
                documentType={DocumentTypeEnum.DELIVERY_NOTE}
                disabled={hasLinked}
                ocrDisabled={hasLinked}
            />
        </Form.Item>
    )

    const rightSide = (
        <>
            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <Form.Item label={t("deliveryNote:drawer.dnNumber")} name="deliveryNoteNumber" rules={deliveryNoteFormRules.deliveryNoteOrderNumber} required>
                        <Input // TODO [WEB-2043] introduce separate constants for DELIVERY_NOTE_NUMBER_MIN/MAX_LENGTH
                            placeholder={t("deliveryNote:drawer.dnNumber")}
                            minLength={PURCHASE_ORDER_NUMBER_MIN_LENGTH}
                            maxLength={PURCHASE_ORDER_NUMBER_MAX_LENGTH}
                            required
                        />
                    </Form.Item>
                </Col>

                <Col span={12}>
                    <Form.Item label={t("deliveryNote:drawer.dateOfIssue")} name="dateOfIssueForm" rules={deliveryNoteFormRules.dateOfIssue} required>
                        <DatePicker className="w-full" disabledDate={gobdNonCompliantDates} format={moment.localeData().longDateFormat("L")} allowClear={false} />
                    </Form.Item>
                </Col>
            </Row>

            <VendorSelection // preserve line breaks
                onVendorChange={onVendorChange}
                selectedVendor={selectedVendor}
                ocrRan={Boolean(currentFileOcr)}
                newVendorSuggestion={mapVendorReadFileAiResponseToVendor(currentFileOcr)}
                showRules={false}
            />

            <LineItemsList lineItems={lineItems} />

            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Form.Item>
                        {lineItems !== undefined && (
                            <PurchaseOrderSelectionButton
                                isLocked={!!preSelectedPurchaseOrder}
                                enabled={!fullyLinked}
                                selectedPurchaseOrders={selectedPurchaseOrders}
                                setSelectedPurchaseOrders={setSelectedPurchaseOrders}
                                vendorId={localVendor}
                                tooltipTitleKey={!fullyLinked ? t("tooltips:delivery_note_fully_linked_hint") : undefined}
                                purchaseOrderFilter={defaultFilterDeliveryNoteLink}
                                tooltipPlacement="left"
                            />
                        )}
                    </Form.Item>
                </Col>
            </Row>

            <Row>
                <Col span={24}>
                    <PurchaseOrderAddressAutocomplete
                        label={t("label:delivery_address")}
                        name="deliveryLocation"
                        placeholder={`${t("placeholder:company_settings.address_line")} (${t("placeholder:company_settings.zip")} ${t("placeholder:company_settings.city")} ${t("placeholder:company_settings.country")})`}
                        onSelect={(e) => {
                            form.setFieldValue("deliveryLocation", e)
                        }}
                        rules={deliveryNoteFormRules.deliveryLocation}
                    />
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Form.Item label={t("label:note")} name="description" rules={deliveryNoteFormRules.description}>
                        <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} maxLength={PURCHASE_ORDER_DESCRIPTION_MAX_LENGTH} />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={[16, 16]} justify="end" className="mt-8 gap-16">
                <Button type="primary" onClick={onSubmit} className="bg-indigo-600 border-indigo-600" data-testid="finway:deliveryNote:submitButton">
                    {shouldShowPurchaseOrderLinkModal ? t("action:po_link.link_items") : t("action:submit")}
                </Button>
            </Row>
        </>
    )

    return (
        <Form layout="vertical" form={form} className="p-12" initialValues={initialValues}>
            <ResponsiveFormLayout leftSide={leftSide} rightSide={rightSide} drawerMode={true} />
        </Form>
    )
}
